import { useState } from 'react'
import { ChevronDown, Lock } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'
import DocumentPreview from './DocumentPreview'
import DocumentSheet from './DocumentSheet'
import ProcessAdminSections from './ProcessAdminSections'
import ProcessOutcome from './ProcessOutcome'
import ProcedureReadinessPanel from './ProcedureReadinessPanel'
import RevisionPreviewPanel from '@/frontend/pages/Dashboard/BusinessProcesses/RevisionPreviewPanel'
import SkillSection from './SkillSection'
import type { BusinessProcessDetail, BusinessProcessVersion } from './types'

function formatDate(value: string | null) {
  if (!value) return 'Ainda não'
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value))
}

function ProcedurePreview({ version }: { version: BusinessProcessVersion | null }) {
  if (!version?.procedure_markdown) {
    return <p className="text-sm text-muted-foreground">O documento aparecerá aqui depois que o agente devolver uma resposta importável.</p>
  }
  return <DocumentPreview content={version.procedure_markdown} testId="procedure-preview" />
}

export default function ProcessAdvancedSection({ process }: { process: BusinessProcessDetail }) {
  const [open, setOpen] = useState(false)
  const version = process.current_version

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="overflow-hidden rounded-xl border border-border bg-card"
      data-testid="process-advanced"
    >
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-3 p-5 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
          data-testid="process-advanced-trigger"
        >
          <ChevronDown
            aria-hidden="true"
            className={cn('size-4 shrink-0 transition-transform motion-reduce:transition-none', !open && '-rotate-90')}
          />
          <span className="min-w-0 flex-1 text-sm font-semibold">Avançado</span>
          <Badge variant="info" className="shrink-0 gap-1" data-testid="process-admin-badge">
            <Lock aria-hidden="true" className="size-3" />
            Apenas admin
          </Badge>
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent className="overflow-hidden data-[state=closed]:[animation:collapse_150ms_ease-in] data-[state=open]:[animation:expand_150ms_ease-out] motion-reduce:[animation:none]">
        <div className="space-y-4 border-t px-4 py-5 sm:px-5">
          {process.revision_preview && <RevisionPreviewPanel key={process.revision_preview.key} preview={process.revision_preview} />}
          <DocumentSheet
            title="Documento do processo"
            subtitle="A versão que as pessoas leem e revisam"
            badge={version?.procedure_approved ? <Badge variant="success">Aprovado</Badge> : undefined}
          >
            {version?.agent_summary && !version.generated && (
              <p className="mb-3 whitespace-pre-wrap text-sm text-muted-foreground">{version.agent_summary}</p>
            )}
            <ProcedurePreview version={version} />
            <ProcedureReadinessPanel version={version} />
            {version?.procedure_approved_at && (
              <p className="mt-3 text-xs text-muted-foreground">
                Aprovado em {formatDate(version.procedure_approved_at)}
                {version.procedure_approved_by_user_name ? ` por ${version.procedure_approved_by_user_name}` : ''}
              </p>
            )}
          </DocumentSheet>

          <div className="flex items-center gap-3">
            <span className="h-px flex-1 bg-border" />
            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">O que o agente recebe</span>
            <span className="h-px flex-1 bg-border" />
          </div>

          <SkillSection version={version} />
          {version?.generated && <ProcessOutcome version={version} />}
          <ProcessAdminSections process={process} />
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}
